/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 *
 ********************************************************************!*/

import { GetterTree } from "vuex";
import { IBotState } from "./state";
import { IRootState } from "@/store/modules/root/state";
import { IFlow } from "@chatcaptain/types/dialog";
import { IEntity, IIntent, IModel, IVersion } from "@chatcaptain/types/chatbot";
import { store } from "@/store";
import { BotStore } from "@/store/constants";
import { IUniPickerCategory, IUniPickerItem } from "@/types";
import { preBuiltEntitiyNames } from "./entities";
import { IChannel } from "@chatcaptain/types/dist/chatbot/channels";
import { IWhatsAppTemplate } from "@/types/WhatsappTemplate";

export enum Getters {
  GET_ID = "GET_ID",
  GET_NAME = "GET_NAME",
  GET_LANGUAGE = "GET_LANGUAGE",
  GET_DEFAULT_LANGUAGE = "GET_DEFAULT_LANGUAGE",
  GET_LANGUAGES = "GET_LANGUAGES",
  GET_LANGUAGE_NAME = "GET_LANGUAGE_NAME",
  GET_FLOWS = "GET_FLOWS",
  GET_FLOW = "GET_FLOW",
  GET_FAQS = "GET_FAQS",
  GET_FAQ = "GET_FAQ",
  GET_ENTITIES = "GET_ENTITIES",
  GET_MODELS = "GET_MODELS",
  GET_VERSIONS = "GET_VERSIONS",
  GET_LIVE_VERSION = "GET_LIVE_VERSION",
  GET_LAST_VERSION = "GET_LAST_VERSION",
  GET_PICKER_VARIABLES = "GET_PICKER_VARIABLES",
  GET_PICKER_VARIABLE = "GET_PICKER_VARIABLE",
  GET_CHANNELS = "GET_CHANNELS",
  GET_TARGET_CHANNEL = "GET_TARGET_CHANNEL",
  GET_WHATSAPP_TEMPLATES = "GET_WHATSAPP_TEMPLATES",
}

export interface IGetters {
  [Getters.GET_ID](state: IBotState): string;
  [Getters.GET_NAME](state: IBotState): string;
  [Getters.GET_LANGUAGE](state: IBotState): string;
  [Getters.GET_DEFAULT_LANGUAGE](state: IBotState): string;
  [Getters.GET_LANGUAGES](state: IBotState): string[];
  [Getters.GET_LANGUAGE_NAME](state: IBotState): (language: string) => string;
  [Getters.GET_FLOWS](state: IBotState): IFlow[];
  [Getters.GET_FLOW](state: IBotState): (flowId: string) => IFlow | undefined;
  [Getters.GET_FAQS](state: IBotState): IIntent[];
  [Getters.GET_FAQ](state: IBotState): (id: string) => IIntent | undefined;
  [Getters.GET_ENTITIES](state: IBotState): IEntity[];
  [Getters.GET_MODELS](state: IBotState): IModel[];
  [Getters.GET_VERSIONS](state: IBotState): IVersion[];
  [Getters.GET_LIVE_VERSION](state: IBotState): number;
  [Getters.GET_LAST_VERSION](state: IBotState): IVersion | null;
  [Getters.GET_PICKER_VARIABLES](state: IBotState): {
    [key: string]: IUniPickerCategory;
  };
  [Getters.GET_PICKER_VARIABLE](
    state: IBotState
  ): (value: string) => IUniPickerItem | undefined;
  [Getters.GET_CHANNELS](state: IBotState): IChannel[];
  [Getters.GET_TARGET_CHANNEL](state: IBotState): IChannel | null;
  [Getters.GET_WHATSAPP_TEMPLATES](
    state: IBotState
  ): IWhatsAppTemplate[] | null;
}

export const getters: GetterTree<IBotState, IRootState> & IGetters = {
  [Getters.GET_ID]: (state: IBotState) => {
    return state.id;
  },
  [Getters.GET_NAME]: (state: IBotState) => {
    return state.name;
  },
  [Getters.GET_LANGUAGE]: (state: IBotState) => {
    return state.language;
  },
  [Getters.GET_DEFAULT_LANGUAGE]: (state: IBotState) => {
    return state.defaultLanguage;
  },
  [Getters.GET_LANGUAGES]: (state: IBotState) => {
    return state.languages;
  },
  [Getters.GET_LANGUAGE_NAME]: (state: IBotState) => (language: string) => {
    return state.languageNames[language] || "Unbekannt";
  },
  [Getters.GET_FLOWS]: (state: IBotState) => {
    return state.flows;
  },
  [Getters.GET_FLOW]: (state: IBotState) => (flowId: string) => {
    if (!state.flows || state.flows.length <= 0)
      store.dispatch(BotStore.Actions.LOAD_FLOWS);
    return state.flows.find((flow: IFlow) => flow.id === flowId);
  },
  [Getters.GET_FAQS]: (state: IBotState) => {
    return state.faqIntents;
  },
  [Getters.GET_FAQ]: (state: IBotState) => (id: string) => {
    if (!state.faqIntents || state.faqIntents.length <= 0)
      store.dispatch(BotStore.Actions.LOAD_FAQS);
    return state.faqIntents.find((faq) => faq.id === id);
  },
  [Getters.GET_ENTITIES]: (state: IBotState) => {
    const entities = state.entities;
    if (!entities) {
      store.dispatch(BotStore.Actions.LOAD_ENTITIES);
      return [];
    }
    entities.forEach((entity) => {
      if (entity.pre_built)
        entity.name = preBuiltEntitiyNames[entity.name] || entity.name;
    });
    return entities;
  },
  [Getters.GET_MODELS]: (state: IBotState) => {
    return state.models;
  },
  [Getters.GET_VERSIONS]: (state: IBotState) => {
    return state.versions;
  },
  [Getters.GET_LIVE_VERSION]: (state: IBotState) => {
    return state.liveVersion;
  },
  [Getters.GET_LAST_VERSION]: (state: IBotState) => {
    return state.lastVersion;
  },
  [Getters.GET_PICKER_VARIABLES]: (state: IBotState) => {
    const variables = state.picker.variables;
    const entities: IUniPickerItem[] = [];
    store.getters[BotStore.Getters.GET_ENTITIES].forEach((entity) => {
      if (entity.pre_built && entity.id == "datetimeV2") {
        entities.push({
          label: "Datum & Zeit -> Datum",
          value: "entity." + entity.id + ".date",
        });
        entities.push({
          label: "Datum & Zeit -> Uhrzeit",
          value: "entity." + entity.id + ".time",
        });
        entities.push({
          label: "Datum & Zeit -> Datum & Uhrzeit",
          value: "entity." + entity.id + ".datetime",
        });
        entities.push({
          label: "Datum & Zeit -> Datumsbereich",
          value: "entity." + entity.id + ".daterange",
        });
        entities.push({
          label: "Datum & Zeit -> Zeitspanne",
          value: "entity." + entity.id + ".timerange",
        });
        entities.push({
          label: "Datum & Zeit -> Datum & Uhrzeitbereich",
          value: "entity." + entity.id + ".datetimerange",
        });
      } else {
        entities.push({
          label: entity.name,
          value: "entity." + entity.id,
        });
      }
    });
    variables.entities = {
      label: "Entitäten",
      items: entities,
    };
    return variables;
  },
  [Getters.GET_PICKER_VARIABLE]: (state: IBotState) => (value: string) => {
    const variables = store.getters[BotStore.Getters.GET_PICKER_VARIABLES];
    for (const category of Object.keys(variables)) {
      const items: IUniPickerItem[] = variables[category].items;
      for (const item of items) {
        if (item.value == value) {
          return item;
        }
      }
    }
    return undefined;
  },
  [Getters.GET_CHANNELS]: (state: IBotState) => {
    return state.channels;
  },
  [Getters.GET_TARGET_CHANNEL]: (state: IBotState) => {
    return state.targetChannel;
  },
  [Getters.GET_WHATSAPP_TEMPLATES]: (state: IBotState) => {
    if (!state.whatsappTemplates) {
      store.dispatch(BotStore.Actions.LOAD_WHATSAPP_TEMPLATES);
    }

    if (state.targetChannel && state.whatsappTemplates) {
      const id = state.targetChannel.id || "";
      if (id in state.whatsappTemplates) return state.whatsappTemplates[id];
    }
    return null;
  },
};
